import { captureException } from "@sentry/react";
import {
  FileBrowserActions,
  FileBrowserFile,
} from "components/FileBrowser/FileBrowser";
import { validateSeriesFiles } from "components/ModalCreateSeries/ModalCreateSeries";
import { ProjectLayoutContextValue } from "components/Project/ProjectLayoutProvider";
import { useDownloadDrsFileNonSeries } from "hooks/useDownloadDrsFileNonSeries";
import { useDrsFileScheduleDeleteDjango } from "hooks/useDrsFileScheduleDeleteDjango";
import { DatasetLayoutContextValue } from "pages/project/dataset/DatasetLayoutProvider";
import { useCallback, useMemo } from "react";
import { useFileUploadContext } from "stores/upload/FileUploadProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { evictCacheFragment } from "utils/cache-fragments";
import { File as DrsFile } from "graphql/_Types";
import { isDefined } from "utils/isDefined";

export const useGetFileBrowserActions = <T extends FileBrowserFile>(
  openModal:
    | ProjectLayoutContextValue["openModal"]
    | DatasetLayoutContextValue["openModal"],
) => {
  const { downloadDrsFileNonSeries } = useDownloadDrsFileNonSeries();
  const { drsFileScheduleDelete } = useDrsFileScheduleDeleteDjango();

  const cancelFileUploads = useFileUploadContext((s) => s.cancelFileUploads);

  const deleteFiles = useCallback(
    async (
      drsFiles: Pick<DrsFile, "id">[],
      onComplete?: ({
        successfulFiles,
        errors,
      }: {
        successfulFiles: Pick<DrsFile, "id">[];
        errors: Error[];
      }) => void,
    ) => {
      const hardDeleteDrsFilesFromCache = (drsFiles: Pick<DrsFile, "id">[]) => {
        const fileIds = drsFiles.map((file) => file.id);
        cancelFileUploads(fileIds);

        drsFiles.forEach((drsFile) =>
          evictCacheFragment({ __typename: "File", id: drsFile.id }),
        );
      };

      const dateDeleted = new Date().toISOString();
      const requests = drsFiles.map(async ({ id }) => {
        try {
          return await drsFileScheduleDelete(id, {
            date_deleted: dateDeleted,
          });
        } catch (err) {
          return new Error(`Failed to delete drsFile ${id}`);
        }
      });

      const drsFilesDeletedResults = await Promise.all(requests);

      const successfullyDeletedFiles = drsFilesDeletedResults.filter(
        function (
          objectOrError,
        ): objectOrError is Awaited<ReturnType<typeof drsFileScheduleDelete>> {
          return !(objectOrError instanceof Error);
        },
      );

      const errors = drsFilesDeletedResults.filter(
        function (result): result is Error {
          return result instanceof Error;
        },
      );

      if (errors.length > 0) {
        addUtilityToastFailure(
          `Failed to delete ${errors.length} out of ${drsFilesDeletedResults.length} files.`,
        );
      } else {
        addUtilityToastSuccess(
          `Successfully deleted ${requests.length} files.`,
        );
      }

      hardDeleteDrsFilesFromCache(successfullyDeletedFiles);
      if (isDefined(onComplete)) {
        onComplete({ successfulFiles: successfullyDeletedFiles, errors });
      }
      return { successfulFiles: successfullyDeletedFiles };
    },
    [cancelFileUploads, drsFileScheduleDelete],
  );

  const actions: FileBrowserActions<T> = useMemo(
    () => ({
      archive: {
        onExecute: (files) => {
          return new Promise((res) => {
            openModal({
              type: "archiveDrsFile",
              props: {
                drsFiles: files.map((file) => ({
                  ...file,
                })),
                onComplete: ({ successfulFiles, errors }) => {
                  res({ successfulFiles, errors });
                },
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            });
          });
        },
        suppressLoading: true,
      },
      createSeries: {
        onExecute: (files) =>
          new Promise((res) => {
            openModal({
              type: "createSeries",
              props: {
                selectedFileIds: files.map(({ id }) => id),
                onComplete: (consumedDrsFiles) =>
                  res({
                    successfulFiles: consumedDrsFiles,
                    errors: [],
                  }),
                onError: (error) =>
                  res({
                    successfulFiles: [],
                    errors: [error],
                  }),
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            });
          }),
        suppressLoading: true,
        isDisabled: (files) =>
          !validateSeriesFiles(
            files.map((file) => ({
              ...file,
            })),
          ).isValid,
        disabledToolTip: (files) =>
          validateSeriesFiles(
            files.map((file) => ({
              ...file,
            })),
          ).validationError,
      },
      revertSeries: {
        onExecute: (files) =>
          new Promise((res) => {
            openModal({
              type: "breakSeries",
              props: {
                drsFiles: files.map(({ source, ...file }) => ({
                  ...file,
                })),
                onComplete: ({ successfulFiles, errors }) =>
                  res({ successfulFiles, errors }),
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            });
          }),
        suppressLoading: true,
        isDisabled: (files) => !files.some((file) => file.isSeries),
        disabledToolTip: "You must select a series to break it.",
      },
      delete: {
        onExecute: (files) =>
          new Promise((res) =>
            openModal({
              type: "deleteDrsFile",
              props: {
                drsFiles: files.map((file) => ({
                  ...file,
                })),
                onComplete: (deleteResults) => {
                  res(deleteResults);
                },
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            }),
          ),
        suppressLoading: true,
      },
      cancel_upload: {
        onExecute: (files) =>
          new Promise((res) => {
            void deleteFiles(files, (result) => res(result));
          }),
      },
      unarchive: {
        onExecute: (files) =>
          new Promise((res) =>
            openModal({
              type: "unarchiveDrsFile",
              props: {
                drsFiles: files.map((file) => ({
                  ...file,
                })),
                onComplete: ({ successfulFiles, errors }) =>
                  res({ successfulFiles, errors }),
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            }),
          ),
        suppressLoading: true,
      },
      rename: {
        onExecute: (files) =>
          new Promise((res) =>
            openModal({
              type: "renameDrsFile",
              props: {
                drsFile: files[0],
                onComplete: (fileRenamed) =>
                  res({
                    successfulFiles: [fileRenamed],
                    errors: [],
                  }),
                onError: (err) =>
                  res({
                    successfulFiles: [],
                    errors: [err],
                  }),
                onClose: () => res({ successfulFiles: [], errors: [] }),
              },
            }),
          ),
        suppressLoading: true,
        isDisabled: (files) => files.length > 1,
        disabledToolTip: "Select only a single file to rename",
      },
      download: {
        suppressLoading: (files) => {
          if (files.length > 0) {
            const file = files[0];
            return file.isSeries;
          }
          return false;
        },
        onExecute: (files) =>
          new Promise((res) => {
            // action is disabled if more than one file selected
            // update suppressLoading if you change this
            const file = files[0];
            if (file.isSeries) {
              openModal({
                type: "downloadFiles",
                props: {
                  files: file.seriesFiles,
                  onClose: () => res({ successfulFiles: [], errors: [] }),
                },
              });
              // opening the modal is considered successful for series files
              res({
                successfulFiles: files,
                errors: [],
              });
            } else {
              downloadDrsFileNonSeries({
                ...file,
              })
                .then(() => {
                  addUtilityToastSuccess("Successfully queued file download");
                  res({ successfulFiles: [file], errors: [] });
                })
                .catch((err) => {
                  captureException(err);
                  addUtilityToastFailure("Failed to queue file download");
                  res({
                    successfulFiles: [],
                    errors: [new Error("Failed to download file")],
                  });
                });
            }
          }),
        isDisabled: (files) => files.length > 1,
        disabledToolTip: "Only one file may be downloaded at a time",
      },
    }),
    [deleteFiles, downloadDrsFileNonSeries, openModal],
  );

  return { actions };
};
